// src/pages/FormsBuilder.jsx
import React from "react";
import axios from "axios";
import { useDrop } from "react-dnd";
import { NativeTypes } from "react-dnd-html5-backend";
import { auth } from "../firebase-config";
import { Box, Divider, Typography, Stack, CircularProgress } from "@mui/material";
import PageHeader from "../components/ui/PageHeader";
import Sidebar from "../components/ui/Sidebar";
import ButtonComponent from "../components/ui/ButtonComponent";

import Palette, { FIELD_TYPES, STRUCTURAL_TYPES } from "../components/formBuilder/Palette";
import Canvas from "../components/formBuilder/Canvas";
import Inspector from "../components/formBuilder/Inspector";
import TemplatesDrawer from "../components/formBuilder/TemplatesDrawer";
import VersionsDrawer from "../components/formBuilder/VersionsDrawer";
import AIBuilderModal from "../components/formBuilder/AIBuilderModal";
import FormPreview from "../components/formBuilder/FormPreview";
import CanvasSkeleton from "../components/formBuilder/CanvasSkeleton";
import { useToast } from "../components/ui/ToastContext";

// ---- CF endpoints ----
const UPSERT_URL =
  process.env.REACT_APP_UPSERT_FORM_TEMPLATE_URL ||
  "https://upsertformtemplate-774073159618.us-central1.run.app";
const PUBLISH_URL =
  process.env.REACT_APP_PUBLISH_FORM_TEMPLATE_URL ||
  "https://publishformtemplate-774073159618.us-central1.run.app";
const GET_DEF_URL =
  process.env.REACT_APP_GET_FORM_DEFINITION_URL ||
  "https://getformdefinition-774073159618.us-central1.run.app";
const EXTRACT_FORM_URL =
  process.env.REACT_APP_EXTRACT_FORM_URL ||
  "https://extractform-774073159618.us-central1.run.app";
const OPTIMIZE_LAYOUT_URL =
  process.env.REACT_APP_OPTIMIZE_LAYOUT_URL ||
  "https://optimizelayout-774073159618.us-central1.run.app";
const LIST_FORM_VERSIONS =
  process.env.REACT_APP_LIST_FORM_VERSIONS ||
  "https://listformversions-774073159618.us-central1.run.app";

// Simple id generator
const uid = (p = "fld") => `${p}_${Math.random().toString(36).slice(2, 10)}`;

const DEFAULT_SCHEMA = {
  title: "Untitled Form",
  description: "",
  fields: [],
};

const isEditableElement = (el) => {
  if (!el) return false;
  const tag = String(el.tagName || "").toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable;
};

export default function FormsBuilder() {
  const { addToast } = useToast();

  const [schema, setSchema] = React.useState(DEFAULT_SCHEMA);
  const [formId, setFormId] = React.useState(null);

  const [templateStatus, setTemplateStatus] = React.useState("draft"); // draft | published
  const [lockedPublished, setLockedPublished] = React.useState(false);
  const [lockedSourceId, setLockedSourceId] = React.useState(null);

  const [versionCount, setVersionCount] = React.useState(0);
  const [versionsBusy, setVersionsBusy] = React.useState(false);

  // ---- IMPORTANT: keep selected field in a ref synchronously ----
  const [selectedFieldId, _setSelectedFieldId] = React.useState(null);
  const selectedFieldIdRef = React.useRef(null);
  const setSelectedFieldId = React.useCallback((nextId) => {
    selectedFieldIdRef.current = nextId;
    _setSelectedFieldId(nextId);
  }, []);

  const [isSaving, setIsSaving] = React.useState(false);
  const [isPublishing, setIsPublishing] = React.useState(false);
  const [isOptimizing, setIsOptimizing] = React.useState(false);
  const [loadingTemplate, setLoadingTemplate] = React.useState(false);

  const [templatesOpen, setTemplatesOpen] = React.useState(false);
  const [versionsOpen, setVersionsOpen] = React.useState(false);
  const [aiModalOpen, setAiModalOpen] = React.useState(false);

  const [previewMode, setPreviewMode] = React.useState(false);

  // Inline insertion (between rows) support (still here, but palette behavior no longer depends on it)
  const [insertIndex, setInsertIndex] = React.useState(null);

  const selectedField = React.useMemo(
    () => (schema.fields || []).find((f) => f.id === selectedFieldId) || null,
    [schema.fields, selectedFieldId]
  );

  const isReadOnly = lockedPublished;

  // ---- Helper: refresh version count for a template ----
  const refreshVersionCount = React.useCallback(async (templateId) => {
    if (!templateId) return;
    try {
      setVersionsBusy(true);
      const idToken = await auth.currentUser.getIdToken(true);
      const { data } = await axios.post(
        LIST_FORM_VERSIONS,
        { formId: templateId, mode: "count" },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      const count =
        typeof data?.count === "number"
          ? data.count
          : Array.isArray(data?.versions)
          ? data.versions.length
          : Array.isArray(data?.items)
          ? data.items.length
          : 0;
      setVersionCount(count);
    } catch (e) {
      console.warn("refreshVersionCount failed:", e?.message || e);
      setVersionCount(0);
    } finally {
      setVersionsBusy(false);
    }
  }, []);

  React.useEffect(() => {
    if (formId) refreshVersionCount(formId);
  }, [formId, refreshVersionCount]);

  // ---------- AI Feature: Build from Upload ----------
  const handleFileDrop = React.useCallback(
    async (files) => {
      const file = files[0];
      if (!file) return;

      if (file.type !== "application/pdf") {
        addToast("Please upload a PDF file.", "warning");
        return;
      }

      const formData = new FormData();
      formData.append("formDocument", file);

      setLoadingTemplate(true);
      try {
        const idToken = await auth.currentUser.getIdToken(true);
        const { data } = await axios.post(EXTRACT_FORM_URL, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${idToken}`,
          },
        });

        if (data.schema) {
          setSchema(data.schema);
          setFormId(null);
          setTemplateStatus("draft");
          setLockedPublished(false);
          setLockedSourceId(null);
          setSelectedFieldId(null);
          addToast("Form extracted from document!", "success");
        } else {
          throw new Error("Extraction failed to produce a valid schema.");
        }
      } catch (err) {
        addToast(err.message || "Failed to extract form from document.", "error");
      } finally {
        setLoadingTemplate(false);
      }
    },
    [addToast, setSelectedFieldId]
  );

  const [{ canDrop, isOver }, drop] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop(item) {
        if (item.files) handleFileDrop(item.files);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [handleFileDrop]
  );

  // ---------- Optimize Layout ----------
  const handleOptimizeLayout = async () => {
    if (isReadOnly) {
      addToast("Published templates are read-only. Clone to edit.", "warning");
      return;
    }
    if ((schema.fields || []).length < 2) {
      addToast("Add at least two fields to optimize the layout.", "info");
      return;
    }
    setIsOptimizing(true);
    try {
      const idToken = await auth.currentUser.getIdToken(true);
      const { data } = await axios.post(
        OPTIMIZE_LAYOUT_URL,
        { schema },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      if (data.schema) {
        setSchema(data.schema);
        addToast("Layout optimized by Kinova!", "success");
      } else {
        throw new Error("Optimization failed to return a schema.");
      }
    } catch (err) {
      addToast(err.message || "Could not optimize layout.", "error");
    } finally {
      setIsOptimizing(false);
    }
  };

  // ---------- Build from Description ----------
  const handleGenerateFromAI = (aiSchema) => {
    setSchema(aiSchema);
    setFormId(null);
    setTemplateStatus("draft");
    setLockedPublished(false);
    setLockedSourceId(null);
    setSelectedFieldId(null);
  };

  // ---------- Saved Templates Drawer ----------
  const openTemplates = () => setTemplatesOpen(true);
  const closeTemplates = () => setTemplatesOpen(false);

  const handleLoadTemplate = async (tid) => {
    try {
      setLoadingTemplate(true);
      const idToken = await auth.currentUser.getIdToken(true);
      const { data } = await axios.post(
        GET_DEF_URL,
        { formId: tid },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      const def = data?.form;
      if (!def) throw new Error("Template not found");

      const status = def.status || "draft";

      setFormId(def.id);
      setTemplateStatus(status);
      setLockedPublished(status === "published");
      setLockedSourceId(status === "published" ? def.id : null);

      setVersionCount(def.versionCount || 0);
      setSchema({
        title: def.name || def.title || "Untitled Form",
        description: def.description || "",
        fields: def?.schema?.fields || [],
        noteSections: def?.schema?.noteSections || def?.noteSections || [],
      });

      setSelectedFieldId(null);
      addToast("Template loaded.", "success");
      setTemplatesOpen(false);
    } catch (e) {
      console.error(e);
      addToast("Failed to load template.", "error");
    } finally {
      setLoadingTemplate(false);
    }
  };

  // ---------- Versions Panel ----------
  const closeVersions = () => setVersionsOpen(false);

  const handleLoadVersion = async (templateId, versionId) => {
    if (!templateId || !versionId) return;
    try {
      setLoadingTemplate(true);
      const idToken = await auth.currentUser.getIdToken(true);
      const { data } = await axios.post(
        GET_DEF_URL,
        { formId: templateId, versionId },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      const def = data?.form;
      if (!def) throw new Error("Version not found");

      const status = def.status || templateStatus || "draft";

      setFormId(def.id);
      setTemplateStatus(status);
      setLockedPublished(status === "published");
      setLockedSourceId(status === "published" ? def.id : null);

      setSchema({
        title: def.name || def.title || "Untitled Form",
        description: def.description || "",
        fields: def?.schema?.fields || [],
        noteSections: def?.schema?.noteSections || [],
      });

      setSelectedFieldId(null);
      addToast(`Loaded version ${versionId}.`, "success");
      setVersionsOpen(false);
    } catch (e) {
      console.error(e);
      addToast("Failed to load version.", "error");
    } finally {
      setLoadingTemplate(false);
    }
  };

  // ---------- Builder actions ----------
  const uidLocal = uid;

  /**
   * FIX: If atIndex is not provided, insert AFTER selected field (or at end if none selected).
   */
  const addField = React.useCallback(
    (type, atIndex = null, options = {}) => {
      if (isReadOnly) {
        addToast("Published templates are read-only. Clone to edit.", "warning");
        return null;
      }

      const safeIndex = (fields, idx) => {
        if (idx == null || idx < 0 || idx > fields.length) return fields.length;
        return idx;
      };

      // If palette didn't pass an index, compute it:
      let computedIndex = atIndex;

      if (computedIndex == null) {
        const fields = schema.fields || [];
        const selectedId = selectedFieldIdRef.current;

        if (selectedId) {
          const selectedIdx = fields.findIndex((f) => f.id === selectedId);
          computedIndex = selectedIdx >= 0 ? selectedIdx + 1 : fields.length;
        } else {
          computedIndex = fields.length;
        }
      }

      // Containers / structural
      if (type === "row") {
        const id = uidLocal("row");
        const row = { id, type: "row", title: "Row", columns: 2 };
        setSchema((prev) => {
          const fields = [...(prev.fields || [])];
          fields.splice(safeIndex(fields, computedIndex), 0, row);
          return { ...prev, fields };
        });
        setSelectedFieldId(id);
        return id;
      }

      if (type === "column") {
        const id = uidLocal("col");
        const col = { id, type: "column", title: "Column", columns: 1 };
        setSchema((prev) => {
          const fields = [...(prev.fields || [])];
          fields.splice(safeIndex(fields, computedIndex), 0, col);
          return { ...prev, fields };
        });
        setSelectedFieldId(id);
        return id;
      }

      if (type === "divider") {
        const id = uidLocal("div");
        const div = { id, type: "divider", label: "Divider" };
        setSchema((prev) => {
          const fields = [...(prev.fields || [])];
          fields.splice(safeIndex(fields, computedIndex), 0, div);
          return { ...prev, fields };
        });
        setSelectedFieldId(id);
        return id;
      }

      if (type === "section") {
        const id = uidLocal("sec");
        const block = { id, type: "section", title: "Section", subtitle: "" };
        setSchema((prev) => {
          const fields = [...(prev.fields || [])];
          fields.splice(safeIndex(fields, computedIndex), 0, block);
          return { ...prev, fields };
        });
        setSelectedFieldId(id);
        return id;
      }

      // Normal fields
      const fieldsLen = (schema.fields || []).length;
      const id = uidLocal();
      const baseName = `${type}_${fieldsLen + 1}`;

      const newField = {
        id,
        type,
        name: baseName,
        label: baseName.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        required: false,
        placeholder: "",
        helperText: "",
        options:
          type === "select" || type === "multiselect" || type === "radio"
            ? [{ label: "Option 1", value: "option1" }]
            : undefined,
        default: type === "checkbox" ? false : "",
        inline: true,
        layout: { colSpan: 1 },

        // grouping support for Row Layout
        groupId: options.groupId || null,
        groupCol: typeof options.groupCol === "number" ? options.groupCol : null,
        groupOrder: typeof options.groupOrder === "number" ? options.groupOrder : Date.now(),

        validationPreset: "none",
        pattern: "",
        patternMessage: "",
      };

      setSchema((prev) => {
        const fields = [...(prev.fields || [])];
        fields.splice(safeIndex(fields, computedIndex), 0, newField);
        return { ...prev, fields };
      });

      setSelectedFieldId(id);
      return id;
    },
    [addToast, isReadOnly, schema.fields, uidLocal, setSelectedFieldId]
  );

  const reorderField = React.useCallback(
    (dragId, hoverId) => {
      if (isReadOnly) return;
      if (dragId === hoverId) return;

      setSchema((prev) => {
        const fields = [...(prev.fields || [])];
        const from = fields.findIndex((f) => f.id === dragId);
        const to = fields.findIndex((f) => f.id === hoverId);
        if (from < 0 || to < 0) return prev;

        const fromItem = fields[from];
        const toItem = fields[to];
        if (
          (fromItem?.groupId || null) !== (toItem?.groupId || null) ||
          (fromItem?.groupCol ?? null) !== (toItem?.groupCol ?? null)
        ) {
          return prev;
        }

        const [moved] = fields.splice(from, 1);
        fields.splice(to, 0, moved);
        return { ...prev, fields };
      });
    },
    [isReadOnly]
  );

  const removeField = React.useCallback(
    (id) => {
      if (isReadOnly) {
        addToast("Published templates are read-only. Clone to edit.", "warning");
        return;
      }

      setSchema((prev) => {
        const fields = [...(prev.fields || [])];
        const item = fields.find((f) => f.id === id);
        const isContainer = item?.type === "row" || item?.type === "column";

        const next = fields
          .filter((f) => f.id !== id)
          .map((f) => {
            if (isContainer && f.groupId === id) {
              return { ...f, groupId: null, groupCol: null };
            }
            return f;
          });

        return { ...prev, fields: next };
      });

      if (selectedFieldId === id) setSelectedFieldId(null);
    },
    [addToast, isReadOnly, selectedFieldId, setSelectedFieldId]
  );

  const duplicateField = React.useCallback(
    (id) => {
      if (isReadOnly) {
        addToast("Published templates are read-only. Clone to edit.", "warning");
        return;
      }
      setSchema((prev) => {
        const fields = [...(prev.fields || [])];
        const idx = fields.findIndex((f) => f.id === id);
        if (idx < 0) return prev;

        const src = fields[idx];
        const copy = {
          ...src,
          id: uidLocal(),
          name: src.name ? `${src.name}_copy` : `copy_${uidLocal("name")}`,
          groupOrder: Date.now(),
        };

        fields.splice(idx + 1, 0, copy);
        return { ...prev, fields };
      });
    },
    [addToast, isReadOnly, uidLocal]
  );

  const updateField = React.useCallback(
    (id, patch) => {
      if (isReadOnly) return;
      setSchema((prev) => ({
        ...prev,
        fields: (prev.fields || []).map((f) => (f.id === id ? { ...f, ...patch } : f)),
      }));
    },
    [isReadOnly]
  );

  const updateSchemaMeta = React.useCallback(
    (patch) => {
      if (isReadOnly) return;
      setSchema((prev) => ({ ...prev, ...patch }));
    },
    [isReadOnly]
  );

  const handleSave = async () => {
    if (isReadOnly) {
      addToast("Published templates are read-only. Use Clone to Edit.", "warning");
      return null;
    }
    try {
      setIsSaving(true);
      const idToken = await auth.currentUser.getIdToken(true);
      const { data } = await axios.post(
        UPSERT_URL,
        { formId, schema },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      if (data?.formId) setFormId(data.formId);
      setTemplateStatus(data?.status || templateStatus || "draft");
      addToast("Form template saved.", "success");
      return data?.formId || formId;
    } catch (e) {
      console.error(e);
      addToast("Failed to save template.", "error");
      throw e;
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (isReadOnly) {
      addToast("Published templates are read-only. Use Clone to Edit.", "warning");
      return;
    }
    try {
      setIsPublishing(true);
      const tid = await handleSave();
      if (!tid) throw new Error("Missing formId after save.");
      const idToken = await auth.currentUser.getIdToken(true);
      await axios.post(PUBLISH_URL, { formId: tid }, { headers: { Authorization: `Bearer ${idToken}` } });

      setTemplateStatus("published");
      setLockedPublished(true);
      setLockedSourceId(tid);

      addToast("Published successfully.", "success");
    } catch (e) {
      console.error(e);
      addToast("Failed to publish.", "error");
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCloneToEdit = () => {
    setFormId(null);
    setTemplateStatus("draft");
    setLockedPublished(false);
    setLockedSourceId(null);

    setSchema((prev) => ({
      ...prev,
      title: prev?.title ? `${prev.title} (Copy)` : "Untitled Form (Copy)",
    }));

    addToast("Cloned. You can now edit and save a new draft.", "success");
  };

  const handleClearCanvas = () => {
    if (isReadOnly) {
      addToast("Published templates are read-only. Clone to edit.", "warning");
      return;
    }
    setSchema(DEFAULT_SCHEMA);
    setFormId(null);
    setTemplateStatus("draft");
    setLockedPublished(false);
    setLockedSourceId(null);
    setVersionCount(0);
    setSelectedFieldId(null);
  };

  // ---------- Keyboard navigation ----------
  const orderedIds = React.useMemo(() => (schema.fields || []).map((f) => f.id), [schema.fields]);

  React.useEffect(() => {
    const onKeyDown = (e) => {
      if (e.defaultPrevented) return;
      if (isEditableElement(e.target)) return;

      const key = e.key;
      const meta = e.metaKey || e.ctrlKey;

      if (key === "Escape") {
        setSelectedFieldId(null);
        return;
      }

      if (key === "ArrowDown" || key === "ArrowUp") {
        e.preventDefault();
        if (!orderedIds.length) return;

        const idx = selectedFieldId ? orderedIds.indexOf(selectedFieldId) : -1;
        const next =
          key === "ArrowDown"
            ? Math.min(orderedIds.length - 1, idx + 1)
            : Math.max(0, idx <= 0 ? 0 : idx - 1);

        setSelectedFieldId(orderedIds[next]);
        return;
      }

      if (key === "Delete" || key === "Backspace") {
        if (!selectedFieldId) return;
        if (isReadOnly) return;
        e.preventDefault();
        removeField(selectedFieldId);
        return;
      }

      if (meta && (key === "d" || key === "D")) {
        if (!selectedFieldId) return;
        if (isReadOnly) return;
        e.preventDefault();
        duplicateField(selectedFieldId);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [duplicateField, isReadOnly, orderedIds, removeField, selectedFieldId, setSelectedFieldId]);

  // Helpful counts for skeleton sizing
  const sectionCount = React.useMemo(
    () => (schema.fields || []).filter((f) => f.type === "section").length || 0,
    [schema.fields]
  );

  const fieldRows = React.useMemo(() => {
    const n =
      (schema.fields || []).filter((f) => f.type !== "section" && f.type !== "divider").length || 6;
    return Math.max(3, Math.ceil(n / 2));
  }, [schema.fields]);

  // ---------- UI ----------
  return (
    <div className="manage-users-container">
      <Sidebar />
      <div className="manage-users-content" ref={drop}>
        <PageHeader
          title="Form Builder"
          actions={[
            <ButtonComponent
              key="ai-modal"
              label="Let Kinova Build It"
              type="secondary"
              onClick={() => setAiModalOpen(true)}
            />,
            <ButtonComponent
              key="ai-optimize"
              label="Optimize Layout"
              type="secondary"
              onClick={handleOptimizeLayout}
              isLoading={isOptimizing}
              disabled={(schema.fields || []).length < 2 || isReadOnly}
            />,
            <ButtonComponent
              key="templates"
              label="Saved Templates"
              type="secondary"
              onClick={openTemplates}
            />,
            formId && (
              <ButtonComponent
                key="versions"
                label={versionsBusy ? "Versions (...)" : `Versions (${versionCount})`}
                type="secondary"
                onClick={() => setVersionsOpen(true)}
                disabled={!formId || versionsBusy}
              />
            ),
            isReadOnly && (
              <ButtonComponent
                key="clone"
                label="Clone to Edit"
                onClick={handleCloneToEdit}
                type="primary"
              />
            ),
            <ButtonComponent
              key="save"
              label="Save Template"
              onClick={handleSave}
              isLoading={isSaving}
              type="primary"
              disabled={isReadOnly}
            />,
            <ButtonComponent
              key="publish"
              label="Publish"
              onClick={handlePublish}
              isLoading={isPublishing}
              type="primary"
              disabled={isReadOnly}
            />,
          ]}
        />

        {loadingTemplate && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading...</Typography>
          </Stack>
        )}

        <Box
          sx={{
            mt: 2,
            opacity: isOver ? 0.85 : 1,
            border: isOver && canDrop ? "2px dashed #1976d2" : "1px solid transparent",
            borderRadius: 2,
            p: isOver && canDrop ? 1 : 0,
            transition: "border .2s ease-in-out, opacity .2s ease-in-out",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "280px minmax(0, 1fr) 340px",
              gap: 2,
              height: "calc(100vh - 180px)",
              pb: 4,
            }}
          >
            {/* Palette */}
            <Box
              sx={{
                position: "sticky",
                top: 16,
                height: "fit-content",
                maxHeight: "calc(100vh - 220px)",
                overflowY: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                p: 2,
                bgcolor: "white",
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Palette
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Palette onAdd={addField} insertIndex={insertIndex} disabled={isReadOnly} />
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Tip: Use Sections to add headings and group fields. Use Row Layout to place fields
                in a specific row group.
              </Typography>
            </Box>

            {/* Canvas / Preview */}
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                p: 2,
                bgcolor: "white",
                minHeight: 500,
                overflowY: "auto",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                <ButtonComponent
                  label={previewMode ? "Back to Canvas" : "Preview Form"}
                  type="secondary"
                  onClick={() => setPreviewMode((v) => !v)}
                  disabled={loadingTemplate}
                />
              </Box>

              {loadingTemplate ? (
                <CanvasSkeleton showHeader sectionCount={sectionCount || 1} fieldRows={fieldRows} minHeight={500} />
              ) : (
                <>
                  {isOver && canDrop && !previewMode && (
                    <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
                      <Typography variant="h6">Drop PDF here to build form</Typography>
                    </Stack>
                  )}

                  {(!isOver || !canDrop) &&
                    (previewMode ? (
                      <FormPreview schema={schema} />
                    ) : (
                      <Canvas
                        schema={schema}
                        setSelectedFieldId={setSelectedFieldId}   // ✅ wrapped setter
                        selectedFieldId={selectedFieldId}
                        onAdd={addField}
                        onSetInsertIndex={setInsertIndex}
                        onReorder={reorderField}
                        onRemove={removeField}
                        onDuplicate={duplicateField}
                        onUpdateField={updateField}
                        onClear={handleClearCanvas}
                        readOnly={isReadOnly}
                      />
                    ))}
                </>
              )}
            </Box>

            {/* Inspector */}
            <Box
              sx={{
                position: "sticky",
                top: 16,
                maxHeight: "calc(100vh - 220px)",
                overflowY: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                p: 2,
                bgcolor: "white",
              }}
            >
              <Inspector
                schema={schema}
                selectedField={selectedField}
                onUpdateField={updateField}
                onUpdateSchemaMeta={updateSchemaMeta}
                readOnly={isReadOnly}
                lockedSourceId={lockedSourceId}
                fieldTypes={FIELD_TYPES}
                structuralTypes={STRUCTURAL_TYPES}
              />
            </Box>
          </Box>
        </Box>

        {/* Modals and Drawers */}
        <AIBuilderModal
          open={aiModalOpen}
          onClose={() => setAiModalOpen(false)}
          onComplete={handleGenerateFromAI}
        />
        <TemplatesDrawer
          open={templatesOpen}
          onClose={closeTemplates}
          onSelectTemplate={handleLoadTemplate}
        />
        <VersionsDrawer
          open={versionsOpen}
          onClose={closeVersions}
          template={{ id: formId, name: schema.title }}
          onSelectVersion={(v) => handleLoadVersion(formId, v.id)}
        />
      </div>
    </div>
  );
}